import sloganImage from '@/assets/img/login/slogan_color@2x.png';
import { breakpoint } from '@/helpers/BreakpointHelper';
import React, { FC, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

interface LoginLoadingProps {
  snsType?: 'kakao' | 'naver' | null;
}

const wave = keyframes`
  0%, 60%, 100% {
    opacity: 0.24;
  }
  30% {
    opacity: 0.9;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 150px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  ${breakpoint('mobile')} {
    top: 56px;
    background: rgba(255, 255, 255, 0.95);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  margin-top: 198px;

  ${breakpoint('mobile')} {
    gap: 32px;
    margin-top: 137px;
  }
`;

const Title = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 200%;
  text-align: center;
  letter-spacing: -0.8px;
  color: #2d2926;

  ${breakpoint('mobile')} {
    font-size: 20px;
    line-height: 155%;
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  ${breakpoint('mobile')} {
    gap: 24px;
  }
`;

const SloganImage = styled.img`
  width: 461px;
  height: 50px;
  object-fit: contain;

  ${breakpoint('mobile')} {
    width: 277px;
    height: 30px;
  }
`;

const DotsAndText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;

  ${breakpoint('mobile')} {
    gap: 32px;
  }
`;

const DotsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;

  ${breakpoint('mobile')} {
    gap: 4px;
  }
`;

const Dot = styled.span<{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1ab6f1;
  opacity: 0.24;
  animation: ${wave} 1.2s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}s;
`;

const SubText = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  text-align: center;
  letter-spacing: -0.8px;
  color: #2d2926;

  ${breakpoint('mobile')} {
    line-height: 150%;
  }

  strong {
    font-weight: 700;
  }
`;

const SubTextGap = styled.div`
  height: 16px;
`;

const LoginLoading: FC<LoginLoadingProps> = () => {

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <Overlay>
      <Content>
        <Title>로그인 정보를 확인하고 있어요</Title>
        <BottomSection>
          <SloganImage
            src={sloganImage}
            alt="어린이, 그 하나로 충분한사명"
          />
          <DotsAndText>
            <DotsWrapper>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Dot key={i} delay={i * 0.1} />
              ))}
            </DotsWrapper>
            <SubText>
              <strong>로그인 중</strong>입니다.
              <SubTextGap />
              잠시만 기다려주세요.
            </SubText>
          </DotsAndText>
        </BottomSection>
      </Content>
    </Overlay>,
    document.body,
  );
};

export default LoginLoading;
